import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";

// AG Grid (v34)
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";

// Chakra UI
import {
  Box,
  Heading,
  HStack,
  Select,
  Flex,
  Image,
  Stack,
  useToast,
  FormControl,
  FormLabel,
  SimpleGrid,
  Tooltip,
  IconButton,
  Button,
  Code,
  Spinner,
  Input,
  Text,
  Badge,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";

import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import CodesetSelect from "./CodesetSelect";
import AutoResizeTextarea from "./AutoResizeTextarea";
import { useAuthenticatedRequest } from "./hooks/useAuthenticatedRequest";
import { useAuth } from "./AuthContext";
import { getDefaultOwnerOrganisationId } from "./utils/ownership";
import { errorFromResponse } from "./utils/apiErrors";
import { HiOutlineUpload, HiOutlineDocumentText, HiOutlineRefresh } from "react-icons/hi";
import { MdClose } from "react-icons/md";
import ReactJson from "react-json-view";

// ---- register AG Grid modules AFTER all imports ----
ModuleRegistry.registerModules([AllCommunityModule]);

const ActionCellRenderer = (props) => {
  const { data } = props;
  return (
    <Tooltip
      label="Load this history item into the form"
      aria-label="Load History Item tooltip">
      <IconButton
        icon={<HiOutlineUpload />}
        size="sm"
        aria-label="Load History Item"
        onClick={() => props.context?.handleLoadHistory?.(data._id)}
      />
    </Tooltip>
  );
};

const Codify = ({ embedded = false }) => {
  const [codifyDefinitions, setCodifyDefinitions] = useState([]);
  const [selectedDefinition, setSelectedDefinition] = useState("");
  const [codifyDefinition, setCodifyDefinition] = useState(null);
  const [codifyHistory, setCodifyHistory] = useState([]);
  const [totalHistoryCount, setTotalHistoryCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedFile, setSelectedFile] = useState(null);
  const [inputMode, setInputMode] = useState("text"); // "text" or "file"
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const gridRef = useRef(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState(60); // percentage
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);
  const [topPanelHeight, setTopPanelHeight] = useState(50); // percentage
  const [isVResizing, setIsVResizing] = useState(false);
  const leftPanelRef = useRef(null);

  const [formData, setFormData] = useState({
    collection: "",
    type: "",
    inputText: "",
    outputDataStandard: "",
    outputDataType: "",
    model: "",
  });

  const [response, setResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const toast = useToast();
  const { get, post, getHeaders } = useAuthenticatedRequest();
  const { user } = useAuth();
  // Codify needs the organisation the request is for. There is no picker on
  // this screen, so only a user with exactly one organisation can be resolved;
  // for others the API answers 400 OWNER_ORGANISATION_REQUIRED.
  const ownerOrganisationId = getDefaultOwnerOrganisationId(user);

  const refreshCodifyDefinitions = async () => {
    try {
      const response = await get("https://codify.poc.mrc.ai/api/definitions/headers");
      setCodifyDefinitions(response.data);
    } catch {
      toast({
        title: "Error refreshing codify definitions",
        status: "error",
        duration: 3000,
      });
    }
  };

  useEffect(() => {
    refreshCodifyDefinitions();
  }, []);

  const handleLoadDefinition = async (definitionId) => {
    if (!definitionId) return;
    try {
      const response = await get(
        `https://codify.poc.mrc.ai/api/definitions/${definitionId}`
      );
      const data = response.data;
      setCodifyDefinition(data);

      setFormData((prev) => {
        const next = {
          ...prev,
          collection: data.collection || prev.collection,
          type: data.type || prev.type,
        };

        const jsonDef = Array.isArray(data.dataDefinitions)
          ? data.dataDefinitions.find(
              (d) =>
                (d.dataType && d.dataType.toLowerCase() === "json") ||
                (d.dataStandard && d.dataStandard.toLowerCase() === "json")
            )
          : null;

        if (jsonDef) {
          next.outputDataStandard = jsonDef.dataStandard || "json";
          next.outputDataType = jsonDef.dataType || "json";
        }
        return next;
      });
    } catch {
      toast({
        title: "Error fetching definition",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleChange = (nameOrEvent, value) => {
    if (typeof nameOrEvent === "string") {
      setFormData((prev) => ({ ...prev, [nameOrEvent]: value }));
    } else {
      const { name, value: inputVal } = nameOrEvent.target;
      setFormData((prev) => ({ ...prev, [name]: inputVal }));
    }
  };

  const validateFile = (file) => {
    const validExtensions = [
      ".txt",
      ".text",
      ".pdf",
      ".docx",
      ".doc",
      ".xlsx",
      ".xls",
      ".pptx",
      ".ppt",
      ".json",
      ".xml",
      ".csv",
    ];
    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      toast({
        title: "Invalid file type",
        description: `Please upload one of: ${validExtensions.join(", ")}`,
        status: "error",
        duration: 5000,
      });
      return false;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: "Maximum file size is 10MB",
        status: "error",
        duration: 5000,
      });
      return false;
    }

    return true;
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
      setInputMode("file");
      // Clear text input when file is selected
      setFormData((prev) => ({ ...prev, inputText: "" }));
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragging to false if we're leaving the drop zone entirely
    if (e.target === dropZoneRef.current) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
        setInputMode("file");
        setFormData((prev) => ({ ...prev, inputText: "" }));
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isResizing || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newLeftWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

      // Constrain between 30% and 70%
      const constrainedWidth = Math.max(30, Math.min(70, newLeftWidth));
      setLeftPanelWidth(constrainedWidth);
    },
    [isResizing]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleVMouseDown = (e) => {
    e.preventDefault();
    setIsVResizing(true);
  };

  const handleVMouseMove = useCallback(
    (e) => {
      if (!isVResizing || !leftPanelRef.current) return;

      const panelRect = leftPanelRef.current.getBoundingClientRect();
      const newTopHeight = ((e.clientY - panelRect.top) / panelRect.height) * 100;

      const constrained = Math.max(20, Math.min(80, newTopHeight));
      setTopPanelHeight(constrained);
    },
    [isVResizing]
  );

  const handleVMouseUp = useCallback(() => {
    setIsVResizing(false);
  }, []);

  useEffect(() => {
    if (isVResizing) {
      document.addEventListener("mousemove", handleVMouseMove);
      document.addEventListener("mouseup", handleVMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleVMouseMove);
        document.removeEventListener("mouseup", handleVMouseUp);
      };
    }
  }, [isVResizing, handleVMouseMove, handleVMouseUp]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResponse(null);
    setIsLoading(true);

    try {
      let result;

      if (inputMode === "file" && selectedFile) {
        // File upload endpoint
        const formDataToSend = new FormData();
        formDataToSend.append("file", selectedFile);

        // Build query string - backend expects these as query params (not [FromForm])
        const queryParams = new URLSearchParams({
          collection: formData.collection,
          type: formData.type,
          outputDataStandard: formData.outputDataStandard,
          outputDataType: formData.outputDataType,
          model: formData.model,
        });
        if (ownerOrganisationId) {
          queryParams.set("organisationOwnerId", ownerOrganisationId);
        }

        // Debug logging
        console.log("Request being sent:", {
          file: selectedFile.name,
          collection: formData.collection,
          type: formData.type,
          outputDataStandard: formData.outputDataStandard,
          outputDataType: formData.outputDataType,
          model: formData.model,
        });

        const headers = getHeaders();
        const res = await fetch(
          `https://codify.poc.mrc.ai/api/codifyv3/file?${queryParams}`,
          {
            method: "POST",
            headers: { 'Authorization': headers['Authorization'] },
            body: formDataToSend,
          }
        );

        if (!res.ok) {
          const apiError = await errorFromResponse(res, `HTTP error ${res.status}`);
          console.error("API Error Response:", apiError.response?.data);
          throw apiError;
        }

        const data = await res.json();
        result = data.result; // The API wraps the result

        toast({
          title: "File processed successfully",
          description: `Extracted ${data.extractedTextLength} characters from ${data.fileName}`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Text input endpoint (existing)
        const payload = {
          Collection: formData.collection,
          Type: formData.type,
          InputText: formData.inputText,
          OutputDataStandard: formData.outputDataStandard,
          OutputDataType: formData.outputDataType,
          Model: formData.model,
          ...(ownerOrganisationId && { OrganisationOwnerId: ownerOrganisationId }),
        };

        const response = await post("https://codify.poc.mrc.ai/api/codifyv3", payload);
        result = response.data;

        toast({
          title: "Codify request successful",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      setResponse(result);
      await refreshCodifyHistory();
    } catch (err) {
      if (err.name === "AbortError") {
        toast({
          title: "Request was cancelled",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error calling Codify API",
          description: err.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCollectionChange = async (selected) => {
    const newCollection = selected?.code || "";
    setFormData((prev) => ({ ...prev, collection: newCollection, type: "" }));
    setSelectedDefinition("");
    setCodifyDefinition(null);

    if (newCollection) {
      try {
        const response = await get(
          `https://codify.poc.mrc.ai/api/definitions/headers?collection=${newCollection}`
        );
        setCodifyDefinitions(response.data);
      } catch {
        toast({
          title: "Error fetching definitions for collection",
          status: "error",
          duration: 3000,
        });
      }
    } else {
      setCodifyDefinitions([]);
    }
  };

  const refreshCodifyHistory = async (page = currentPage, size = pageSize) => {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: size.toString(),
        sortBy: "CreatedAt",
        sortDescending: "true"
      });

      const response = await get(`https://codify.poc.mrc.ai/api/history?${queryParams}`);
      const data = response.data;
      setCodifyHistory(data.items || []);
      setTotalHistoryCount(data.pagination?.totalCount || 0);
    } catch {
      toast({
        title: "Error refreshing codify history",
        status: "error",
        duration: 3000,
      });
    }
  };

  useEffect(() => {
    refreshCodifyHistory();
  }, []);

  // Fetch data when page or page size changes
  useEffect(() => {
    refreshCodifyHistory(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const handleLoadHistory = async (historyId) => {
    if (!historyId) return;
    try {
      const response = await get(
        `https://codify.poc.mrc.ai/api/history/${historyId}`
      );
      const data = response.data;

      setFormData({
        collection: data.collection || "",
        type: "",
        inputText: data.inputText || "",
        outputDataStandard: data.outputDataStandard || "",
        outputDataType: data.outputDataType || "",
        model: data.model || "",
      });

      // Clear file selection when loading history (it's text-based)
      setSelectedFile(null);
      setInputMode("text");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (data.collection) {
        await handleCollectionChange({ code: data.collection });
      }

      setFormData((prev) => ({
        ...prev,
        type: data.type || "",
      }));

      if (data.output) {
        setResponse({ codifiedJSON: data.output });
      } else {
        setResponse(null);
      }

      toast({
        title: "History item loaded into form",
        status: "info",
        duration: 2000,
      });
    } catch {
      toast({
        title: "Error fetching history item",
        status: "error",
        duration: 3000,
      });
    }
  };

  const columnDefs = React.useMemo(
    () => [
      {
        headerName: "",
        field: "_id",
        width: 60,
        maxWidth: 60,
        cellRenderer: ActionCellRenderer,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Type",
        field: "type",
        sortable: true,
        filter: true,
        minWidth: 200,
      },
      {
        headerName: "Collection",
        field: "collection",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Output Standard",
        field: "outputDataStandard",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Output Type",
        field: "outputDataType",
        sortable: true,
        filter: true,
        minWidth: 150,
      },
      {
        headerName: "Model",
        field: "model",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Prompt Tokens",
        field: "promptTokens",
        sortable: true,
        filter: true,
        minWidth: 120,
        valueFormatter: (p) => p.value ? p.value.toLocaleString() : "",
        cellStyle: { textAlign: 'right' },
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: "Completion Tokens",
        field: "completionTokens",
        sortable: true,
        filter: true,
        minWidth: 150,
        valueFormatter: (p) => p.value ? p.value.toLocaleString() : "",
        cellStyle: { textAlign: 'right' },
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: "Total Tokens",
        field: "totalTokens",
        sortable: true,
        filter: true,
        minWidth: 120,
        valueFormatter: (p) => p.value ? p.value.toLocaleString() : "",
        cellStyle: { textAlign: 'right' },
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: "Input Price",
        field: "inputPrice",
        sortable: true,
        filter: true,
        minWidth: 120,
        valueFormatter: (p) => p.value ? `$${p.value.toFixed(4)}` : "",
        cellStyle: { textAlign: 'right' },
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: "Output Price",
        field: "outputPrice",
        sortable: true,
        filter: true,
        minWidth: 120,
        valueFormatter: (p) => p.value ? `$${p.value.toFixed(4)}` : "",
        cellStyle: { textAlign: 'right' },
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: "Input Cost",
        field: "inputCost",
        sortable: true,
        filter: true,
        minWidth: 120,
        valueFormatter: (p) => p.value ? `$${p.value.toFixed(4)}` : "",
        cellStyle: { textAlign: 'right' },
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: "Output Cost",
        field: "outputCost",
        sortable: true,
        filter: true,
        minWidth: 120,
        valueFormatter: (p) => p.value ? `$${p.value.toFixed(4)}` : "",
        cellStyle: { textAlign: 'right' },
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: "Total Cost",
        field: "totalCost",
        sortable: true,
        filter: true,
        minWidth: 120,
        valueFormatter: (p) => p.value ? `$${p.value.toFixed(4)}` : "",
        cellStyle: { textAlign: 'right' },
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: "Created",
        field: "createdAt",
        sortable: true,
        filter: true,
        valueFormatter: (p) =>
          p.value ? new Date(p.value).toLocaleString() : "",
        sort: "desc",
        comparator: (a, b) => new Date(a).getTime() - new Date(b).getTime(),
      },
    ],
    []
  );

  const defaultColDef = React.useMemo(
    () => ({
      resizable: true,
      flex: 1,
      minWidth: 200,
    }),
    []
  );

  const onGridReady = useCallback((params) => {
    params.api.sizeColumnsToFit();
    const handler = () => params.api.sizeColumnsToFit();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const onFirstDataRendered = useCallback((p) => {
    p.api.sizeColumnsToFit();
  }, []);

  const gridContext = React.useMemo(
    () => ({ handleLoadHistory }),
    [handleLoadHistory]
  );

  const content = (
    <Box position="relative" zIndex={1} p={embedded ? 2 : 10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={2}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={2}>
                <Heading size="lg">Codify</Heading>
              </HStack>
            </Flex>
          </Box>

          <HStack w="100%" align="stretch" ref={containerRef} position="relative">
            <Flex w="100%" align="stretch">
              <Flex
                direction="column"
                flex={`0 0 ${leftPanelWidth}%`}
                maxW={`${leftPanelWidth}%`}
                w="100%"
                pr={1}
                maxH="calc(100vh - 200px)"
                ref={leftPanelRef}
                transition={isResizing || isVResizing ? "none" : "flex 0.1s ease, max-width 0.1s ease"}>
                <Box
                  flex={response ? `0 0 ${topPanelHeight}%` : "1"}
                  overflow="auto"
                  borderWidth="1px"
                  borderRadius="md"
                  p={2}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <form onSubmit={handleSubmit}>
                    <Stack spacing={4} mb={2}>
                      <Heading size="md">Request</Heading>

                      <SimpleGrid columns={5} spacing={2}>
                        <CodesetSelect
                          size="sm"
                          label="Collection"
                          codesetCode="CODIFY003"
                          value={
                            formData.collection
                              ? { code: formData.collection, description: "" }
                              : null
                          }
                          onChange={handleCollectionChange}
                        />

                        <FormControl>
                          <FormLabel>Type</FormLabel>
                          <Select
                            size="sm"
                            value={formData.type || ""}
                            onChange={(e) =>
                              setFormData({ ...formData, type: e.target.value })
                            }>
                            <option value="">Select a type...</option>
                            {codifyDefinitions
                              .filter(
                                (d) => d.collection === formData.collection
                              )
                              .sort((a, b) => a.type.localeCompare(b.type))
                              .map((def) => (
                                <option key={def._id} value={def.type}>
                                  {def.type}
                                </option>
                              ))}
                          </Select>
                        </FormControl>

                        <CodesetSelect
                          size="sm"
                          label="Output Data Standard"
                          codesetCode="CODIFY001"
                          value={
                            formData.outputDataStandard
                              ? {
                                  code: formData.outputDataStandard,
                                  description: "",
                                }
                              : null
                          }
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              outputDataStandard: selected?.code || "",
                            }))
                          }
                        />

                        <CodesetSelect
                          size="sm"
                          label="Output Data Type"
                          codesetCode="CODIFY002"
                          value={
                            formData.outputDataType
                              ? {
                                  code: formData.outputDataType,
                                  description: "",
                                }
                              : null
                          }
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              outputDataType: selected?.code || "",
                            }))
                          }
                        />

                        <CodesetSelect
                          size="sm"
                          label="Model"
                          codesetCode="CODIFY006"
                          value={
                            formData.model
                              ? {
                                  code: formData.model,
                                  description: "",
                                }
                              : null
                          }
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              model: selected?.code || "",
                            }))
                          }
                        />
                      </SimpleGrid>

                      {/* Input Mode Tabs */}
                      <Box>
                        <Tabs
                          index={inputMode === "text" ? 0 : 1}
                          onChange={(index) => {
                            const newMode = index === 0 ? "text" : "file";
                            setInputMode(newMode);
                            if (newMode === "text") {
                              handleRemoveFile();
                            } else {
                              setFormData((prev) => ({
                                ...prev,
                                inputText: "",
                              }));
                            }
                          }}>
                          <TabList>
                            <Tab>
                              <HStack>
                                <HiOutlineDocumentText />
                                <Text>Text Input</Text>
                              </HStack>
                            </Tab>
                            <Tab>
                              <HStack>
                                <HiOutlineUpload />
                                <Text>File Upload</Text>
                              </HStack>
                            </Tab>
                          </TabList>

                          <TabPanels>
                            <TabPanel px={0}>
                              <AutoResizeTextarea
                                label="Input Text"
                                placeholder="Enter text to be codified"
                                isCode={true}
                                size="sm"
                                resize="vertical"
                                minHeight="30px"
                                value={formData.inputText || ""}
                                onChange={(e) =>
                                  handleChange("inputText", e.target.value)
                                }
                              />
                            </TabPanel>

                            <TabPanel px={0}>
                              <FormControl>
                                <FormLabel>Upload File</FormLabel>
                                <Box
                                  ref={dropZoneRef}
                                  onDragEnter={handleDragEnter}
                                  onDragOver={handleDragOver}
                                  onDragLeave={handleDragLeave}
                                  onDrop={handleDrop}
                                  border="2px dashed"
                                  borderColor={isDragging ? "blue.500" : "gray.300"}
                                  borderRadius="md"
                                  p={6}
                                  textAlign="center"
                                  bg={isDragging ? "blue.50" : "gray.50"}
                                  transition="all 0.2s"
                                  cursor="pointer"
                                  _hover={{ borderColor: "blue.400", bg: "blue.50" }}
                                  onClick={() => fileInputRef.current?.click()}>
                                  <Input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".txt,.text,.pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.json,.xml,.csv"
                                    onChange={handleFileSelect}
                                    display="none"
                                    id="file-upload"
                                  />
                                  <VStack spacing={2}>
                                    <HiOutlineUpload size={32} color={isDragging ? "#3182CE" : "#A0AEC0"} />
                                    <Text fontWeight="medium" color={isDragging ? "blue.600" : "gray.700"}>
                                      {isDragging ? "Drop file here" : "Drag and drop a file here"}
                                    </Text>
                                    <Text fontSize="sm" color="gray.500">
                                      or
                                    </Text>
                                    <Button
                                      as="span"
                                      leftIcon={<HiOutlineUpload />}
                                      size="sm"
                                      colorScheme="blue"
                                      variant="outline"
                                      onClick={(e) => e.stopPropagation()}>
                                      Choose File
                                    </Button>
                                  </VStack>
                                </Box>
                                {selectedFile && (
                                  <HStack mt={3} spacing={2} p={3} bg="green.50" borderRadius="md">
                                    <Badge colorScheme="green" fontSize="sm">
                                      {selectedFile.name}
                                    </Badge>
                                    <Badge colorScheme="blue" fontSize="sm">
                                      {(selectedFile.size / 1024).toFixed(2)} KB
                                    </Badge>
                                    <IconButton
                                      icon={<MdClose />}
                                      size="xs"
                                      colorScheme="red"
                                      onClick={handleRemoveFile}
                                      aria-label="Remove file"
                                    />
                                  </HStack>
                                )}
                                <Text fontSize="xs" color="gray.600" mt={2}>
                                  Supported: .txt, .text, .pdf, .docx, .doc, .xlsx, .xls, .pptx, .ppt, .json, .xml, .csv (Max 10MB)
                                </Text>
                              </FormControl>
                            </TabPanel>
                          </TabPanels>
                        </Tabs>
                      </Box>
                    </Stack>
                    <Button
                      type="submit"
                      colorScheme="linkedin"
                      isLoading={isLoading}
                      loadingText="Processing..."
                      spinner={<Spinner size="sm" />}
                      isDisabled={
                        isLoading ||
                        (inputMode === "text" && !formData.inputText) ||
                        (inputMode === "file" && !selectedFile)
                      }>
                      Codify
                    </Button>
                  </form>
                </Box>

                {response && (
                  <>
                    {/* Vertical resizable separator */}
                    <Box
                      height="12px"
                      cursor="row-resize"
                      bg={isVResizing ? "blue.400" : "gray.300"}
                      _hover={{ bg: "blue.400" }}
                      onMouseDown={handleVMouseDown}
                      userSelect="none"
                      flexShrink={0}
                      my={1}
                      borderRadius="md"
                      transition={isVResizing ? "none" : "background-color 0.2s"}
                    />
                    <Box
                      flex={`0 0 ${100 - topPanelHeight}%`}
                      overflow="auto"
                      borderWidth="1px"
                      borderRadius="md"
                      p={4}
                      borderColor="gray.500"
                      backgroundColor="whiteAlpha.800">
                      <Heading size="md" mb={2}>
                        Codified Output
                      </Heading>
                      <Box
                        p={2}
                        borderRadius="md"
                        backgroundColor="white"
                        overflow="auto">
                        {(() => {
                          try {
                            const parsed = JSON.parse(response.codifiedJSON);
                            return (
                              <ReactJson
                                src={parsed}
                                theme="rjv-default"
                                collapsed={false}
                                displayDataTypes={false}
                                displayObjectSize={true}
                                enableClipboard={true}
                                name={false}
                              />
                            );
                          } catch {
                            return (
                              <Code display="block" whiteSpace="pre">
                                {response.codifiedJSON || "No codifiedJSON found"}
                              </Code>
                            );
                          }
                        })()}
                      </Box>
                    </Box>
                  </>
                )}
              </Flex>

              {/* Resizable separator */}
              <Box
                width="16px"
                cursor="col-resize"
                bg={isResizing ? "blue.400" : "gray.300"}
                _hover={{ bg: "blue.400" }}
                onMouseDown={handleMouseDown}
                position="relative"
                userSelect="none"
                transition={isResizing ? "none" : "background-color 0.2s"}
                sx={{
                  "&:active": {
                    bg: "blue.500",
                  },
                }}
              />

              <Box
                flex={`0 0 ${100 - leftPanelWidth}%`}
                maxW={`${100 - leftPanelWidth}%`}
                minH="0"
                display="flex"
                flexDirection="column"
                borderWidth="1px"
                borderRadius="md"
                p={2}
                pl={1}
                borderColor="gray.500"
                backgroundColor="whiteAlpha.800"
                transition={isResizing ? "none" : "flex 0.1s ease, max-width 0.1s ease"}>
                <HStack justify="space-between" mb={2}>
                  <Heading size="md">
                    Request History
                  </Heading>
                  <Tooltip label="Refresh history">
                    <IconButton
                      icon={<HiOutlineRefresh />}
                      size="sm"
                      aria-label="Refresh history"
                      onClick={() => refreshCodifyHistory(currentPage, pageSize)}
                    />
                  </Tooltip>
                </HStack>

                <Box
                  overflow="hidden"
                  w="100%"
                  minH="0"
                  h="calc(100vh - 380px)">
                  <div
                    className="ag-theme-quartz"
                    style={{ height: "100%", width: "100%" }}>
                    <AgGridReact
                      ref={gridRef}
                      rowData={codifyHistory ?? []}
                      columnDefs={columnDefs}
                      defaultColDef={defaultColDef}
                      context={gridContext}
                      animateRows
                      pagination={false}
                      onGridReady={onGridReady}
                      onFirstDataRendered={onFirstDataRendered}
                      suppressCellFocus
                    />
                  </div>
                </Box>

                {/* Custom Pagination Controls */}
                <Box
                  mt={2}
                  p={3}
                  bg="white"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="gray.200"
                >
                  <HStack justify="space-between" align="center">
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        isDisabled={currentPage === 1}
                      >
                        First
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        isDisabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setCurrentPage(p => p + 1)}
                        isDisabled={currentPage >= Math.ceil(totalHistoryCount / pageSize)}
                      >
                        Next
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setCurrentPage(Math.ceil(totalHistoryCount / pageSize))}
                        isDisabled={currentPage >= Math.ceil(totalHistoryCount / pageSize)}
                      >
                        Last
                      </Button>
                    </HStack>
                    <HStack spacing={3}>
                      <Text fontSize="sm" fontWeight="medium">
                        Page {currentPage} of {Math.ceil(totalHistoryCount / pageSize) || 1}
                        {' '}({totalHistoryCount} total)
                      </Text>
                      <HStack spacing={2}>
                        <Text fontSize="sm">Items per page:</Text>
                        <Select
                          size="sm"
                          w="80px"
                          value={pageSize}
                          onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                        >
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </Select>
                      </HStack>
                    </HStack>
                  </HStack>
                </Box>
              </Box>
            </Flex>
          </HStack>
    </Box>
  );

  if (embedded) {
    return content;
  }

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />
        {content}
      </Box>
    </Box>
  );
};

export default Codify;
